import { useMemo, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { apiErrorMessage, type ApiError } from "@/lib/api";
import { formatCurrencyFull, formatDate, localToday } from "@/lib/data";
import type { BookingEvent, BookingPayload, BookingRoom, RoomBooking } from "./types";
import { ACTIVE_STATUSES, addDays, d10, dayDiff } from "./status";

interface RoomBookingFormProps {
  initialData?: RoomBooking | null;
  /** Pre-filled choices for a new booking (e.g. "Book this room" from the Rooms page) */
  preset?: { room_id?: number; event_id?: number };
  rooms: BookingRoom[];
  events: BookingEvent[];
  /** All loaded bookings, used to warn about clashes before saving */
  bookings: RoomBooking[];
  /** Throws on failure; the form then stays open and shows the server's message */
  onSubmit: (data: BookingPayload) => Promise<void>;
  onCancel: () => void;
}

const fmtDay = (day: string) => (day ? formatDate(`${day}T00:00:00`) : "");

export default function RoomBookingForm({ initialData, preset, rooms, events, bookings, onSubmit, onCancel }: RoomBookingFormProps) {
  const today = localToday();
  const isEdit = !!initialData?.id;
  const presetRoom = rooms.find((r) => r.id === preset?.room_id);

  const [eventId, setEventId] = useState(String(initialData?.event_id ?? preset?.event_id ?? ""));
  const [roomId, setRoomId] = useState(String(initialData?.room_id ?? preset?.room_id ?? ""));
  const [guestName, setGuestName] = useState(initialData?.guest_name || "");
  const [guestCount, setGuestCount] = useState(String(initialData?.guest_count ?? 1));
  const [checkIn, setCheckIn] = useState(initialData ? d10(initialData.check_in) : today);
  const [checkOut, setCheckOut] = useState(initialData ? d10(initialData.check_out) : addDays(today, 1));
  const [rate, setRate] = useState(initialData ? String(Number(initialData.rate)) : presetRoom ? String(Number(presetRoom.rate)) : "");
  const [notes, setNotes] = useState(initialData?.notes || "");
  const [datesTouched, setDatesTouched] = useState(isEdit);
  const [rateTouched, setRateTouched] = useState(isEdit);

  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  // Cancelled events can't take rooms; keep the booking's own event in the list when editing
  const eventOptions = useMemo(() => {
    const usable = events.filter((e) => e.status !== "cancelled" || e.id === initialData?.event_id);
    const end = (e: BookingEvent) => d10(e.end_date) || d10(e.start_date);
    const ahead = usable.filter((e) => end(e) >= today).sort((a, b) => d10(a.start_date).localeCompare(d10(b.start_date)));
    const past = usable.filter((e) => end(e) < today).sort((a, b) => d10(b.start_date).localeCompare(d10(a.start_date)));
    return { ahead, past };
  }, [events, initialData?.event_id, today]);

  // Rooms under maintenance can't be booked (the booking's current room stays listed)
  const roomOptions = useMemo(
    () => rooms.filter((r) => r.status !== "maintenance" || r.id === initialData?.room_id),
    [rooms, initialData?.room_id]
  );

  const datesValid = !!checkIn && !!checkOut && checkOut > checkIn;
  const nights = datesValid ? dayDiff(checkOut, checkIn) : 0;
  const holdsRoom = !initialData || ACTIVE_STATUSES.includes(initialData.status);

  /** An active booking of this room that overlaps the chosen dates */
  const clashFor = (id: number) =>
    datesValid && holdsRoom
      ? bookings.find((b) =>
          b.room_id === id && b.id !== initialData?.id && ACTIVE_STATUSES.includes(b.status) &&
          d10(b.check_in) < checkOut && d10(b.check_out) > checkIn)
      : undefined;
  const clash = roomId ? clashFor(Number(roomId)) : undefined;

  const handleEventChange = (value: string) => {
    setEventId(value);
    // New booking: start from the event's own dates until the user picks dates
    const ev = events.find((e) => String(e.id) === value);
    if (!ev || datesTouched) return;
    const start = d10(ev.start_date);
    const end = d10(ev.end_date) || start;
    if (start && start >= today) {
      setCheckIn(start);
      setCheckOut(addDays(end > start ? end : start, 1));
    }
  };

  const handleRoomChange = (value: string) => {
    setRoomId(value);
    const room = rooms.find((r) => String(r.id) === value);
    if (room && !rateTouched) setRate(String(Number(room.rate) || ""));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setFieldErrors({});
    if (!datesValid) {
      setFormError("Check-out must be after check-in.");
      return;
    }
    setSaving(true);
    try {
      await onSubmit({
        event_id: Number(eventId),
        room_id: Number(roomId),
        guest_name: guestName.trim(),
        guest_count: Number(guestCount),
        check_in: checkIn,
        check_out: checkOut,
        rate: Number(rate),
        notes: notes.trim() || null,
      });
    } catch (error) {
      setFormError(apiErrorMessage(error, "Couldn't save the booking"));
      setFieldErrors((error as ApiError)?.errors || {});
    } finally {
      setSaving(false);
    }
  };

  const inputClass = (field?: string) =>
    `w-full h-10 px-3 rounded-xl border text-sm bg-white outline-none focus:ring-2 ${
      field && fieldErrors[field]
        ? "border-rose-300 focus:border-rose-400 focus:ring-rose-100"
        : "border-stone-200 focus:border-amber-400 focus:ring-amber-100"
    }`;
  const fieldError = (field: string) =>
    fieldErrors[field]?.[0] ? <p className="text-xs text-rose-600 mt-1">{fieldErrors[field][0]}</p> : null;
  const label = "block text-xs font-medium text-stone-500 mb-1.5";

  const eventLabel = (ev: BookingEvent) =>
    [ev.name, ev.client?.name, ev.start_date ? fmtDay(d10(ev.start_date)) : ""].filter(Boolean).join(" · ");

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="rb-event" className={label}>Event *</label>
        <select id="rb-event" value={eventId} onChange={(e) => handleEventChange(e.target.value)} className={inputClass("event_id")} required>
          <option value="">Select event</option>
          {eventOptions.ahead.length > 0 && (
            <optgroup label="Upcoming">
              {eventOptions.ahead.map((ev) => <option key={ev.id} value={ev.id}>{eventLabel(ev)}</option>)}
            </optgroup>
          )}
          {eventOptions.past.length > 0 && (
            <optgroup label="Past">
              {eventOptions.past.map((ev) => <option key={ev.id} value={ev.id}>{eventLabel(ev)}</option>)}
            </optgroup>
          )}
        </select>
        {fieldError("event_id")}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="rb-in" className={label}>Check-in *</label>
          <input
            id="rb-in"
            type="date"
            value={checkIn}
            onChange={(e) => {
              const v = e.target.value;
              setCheckIn(v);
              setDatesTouched(true);
              if (v && checkOut && checkOut <= v) setCheckOut(addDays(v, 1));
            }}
            className={inputClass("check_in")}
            required
          />
          {fieldError("check_in")}
        </div>
        <div>
          <label htmlFor="rb-out" className={label}>Check-out *</label>
          <input
            id="rb-out"
            type="date"
            value={checkOut}
            min={checkIn ? addDays(checkIn, 1) : undefined}
            onChange={(e) => { setCheckOut(e.target.value); setDatesTouched(true); }}
            className={inputClass("check_out")}
            required
          />
          {fieldError("check_out")}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor="rb-room" className={label}>Room *</label>
          <select id="rb-room" value={roomId} onChange={(e) => handleRoomChange(e.target.value)} className={inputClass("room_id")} required>
            <option value="">Select room</option>
            {roomOptions.map((room) => {
              const busy = clashFor(room.id);
              return (
                <option key={room.id} value={room.id}>
                  {room.name}{room.type ? ` (${room.type})` : ""}
                  {room.status === "maintenance" ? " · maintenance" : busy ? " · already booked" : ""}
                </option>
              );
            })}
          </select>
          {roomOptions.length === 0 && <p className="text-xs text-stone-500 mt-1">No rooms to choose from. Add rooms on the Rooms page first.</p>}
          {fieldError("room_id")}
        </div>
        <div>
          <label htmlFor="rb-rate" className={label}>Rate per night (₹) *</label>
          <input
            id="rb-rate"
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={rate}
            onChange={(e) => { setRate(e.target.value); setRateTouched(true); }}
            placeholder="0"
            className={inputClass("rate")}
            required
          />
          {fieldError("rate")}
        </div>
      </div>

      {clash && (
        <p className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
          <span>
            This room is already booked for {clash.guest_name} ({fmtDay(d10(clash.check_in))} – {fmtDay(d10(clash.check_out))}).
            Pick another room or change the dates.
          </span>
        </p>
      )}

      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2">
          <label htmlFor="rb-guest" className={label}>Guest name *</label>
          <input
            id="rb-guest"
            type="text"
            value={guestName}
            onChange={(e) => setGuestName(e.target.value)}
            placeholder="e.g. Rahul Sharma"
            className={inputClass("guest_name")}
            required
          />
          {fieldError("guest_name")}
        </div>
        <div>
          <label htmlFor="rb-count" className={label}>Guests *</label>
          <input
            id="rb-count"
            type="number"
            inputMode="numeric"
            min="1"
            value={guestCount}
            onChange={(e) => setGuestCount(e.target.value)}
            className={inputClass("guest_count")}
            required
          />
          {fieldError("guest_count")}
        </div>
      </div>

      {nights > 0 && Number(rate) > 0 && (
        <p className="text-sm text-stone-600">
          {nights} night{nights === 1 ? "" : "s"} × {formatCurrencyFull(Number(rate))} ={" "}
          <span className="font-semibold text-stone-800">{formatCurrencyFull(nights * Number(rate))}</span>
        </p>
      )}

      <div>
        <label htmlFor="rb-notes" className={label}>Notes</label>
        <textarea
          id="rb-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Special requests, preferences…"
          rows={3}
          className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100 resize-none"
        />
      </div>

      {formError && (
        <div role="alert" className="flex items-start gap-2 rounded-xl bg-red-50 border border-red-200 px-3 py-2.5 text-sm text-red-700">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{formError}</span>
        </div>
      )}

      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="flex-1 h-11 px-4 rounded-xl bg-stone-100 text-stone-600 font-semibold text-sm hover:bg-stone-200 transition-colors border border-stone-200 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="flex-1 h-11 px-4 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity disabled:opacity-60"
        >
          {saving ? "Saving…" : isEdit ? "Save changes" : "Add booking"}
        </button>
      </div>
    </form>
  );
}
